import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sileo } from "sileo";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import type {
  ApiLocationClosure,
  ApiLocationClosureCreate,
  ApiLocationClosureUpdate,
  PaginatedResponse,
} from "@/types/api";

const CLOSURE_FIELDS = [
  "id",
  "date",
  "end_date",
  "start_time",
  "end_time",
  "reason",
] as const satisfies readonly (keyof ApiLocationClosure)[];

function showApiError(error: unknown): void {
  const data = (error as { data?: { detail?: unknown } }).data;
  const detail = data?.detail;
  if (typeof detail === "string" && detail.length > 0) {
    sileo.error({ description: detail });
    return;
  }
  sileo.error({ description: "Error de conexión. Intentá de nuevo." });
}

export function useClosures() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useQuery({
    queryKey: ["closures", organizationId, locationId],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: CLOSURE_FIELDS.join(",") });
      const res = await api
        .get(`closures/?${params}`)
        .json<PaginatedResponse<ApiLocationClosure>>();
      return res.items;
    },
    enabled: isAuthenticated && !!organizationId && !!locationId,
  });
}

export function useCreateClosure() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (data: ApiLocationClosureCreate) =>
      api.post("closures/", { json: data }).json<ApiLocationClosure>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["closures", organizationId, locationId] });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: ["closures", organizationId, locationId] });
    },
  });
}

export function useUpdateClosure() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: ApiLocationClosureUpdate }) =>
      api.patch(`closures/${id}/`, { json: changes }).json<ApiLocationClosure>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["closures", organizationId, locationId] });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: ["closures", organizationId, locationId] });
    },
  });
}

export function useDeleteClosure() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (id: string) => api.delete(`closures/${id}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["closures", organizationId, locationId] });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: ["closures", organizationId, locationId] });
    },
  });
}
